import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import graspdLogo from '../../assets/graspd-logo.svg';
import '../../styles/splash.css';

const APP_STORE_URL = 'https://apps.apple.com/us/app/graspd/id6759442342';

const Navbar = () => {
    // Lazy-init from the CURRENT scroll position, not a hardcoded false — this
    // component remounts fresh on every marketing-page navigation (it's
    // rendered per-page, not through a shared layout), and React Router does
    // not reset window.scrollY on route change. Without this, landing on a
    // new page already scrolled down renders a transparent nav over content.
    const [scrolled, setScrolled] = useState(() => window.scrollY > 50);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Prevent body scroll when mobile menu is open
    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [mobileMenuOpen]);

    // Handle hash scrolling when navigating from another page. Also resets
    // scroll to the top on a plain (hashless) navigation — React Router does
    // not do this itself, so without it a new page mounts at whatever
    // scrollY the previous page was left at, which can exceed the new page's
    // height and clamp to its bottom.
    useEffect(() => {
        if (location.hash) {
            const id = location.hash.replace('#', '');
            setTimeout(() => {
                const element = document.getElementById(id);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);
        } else {
            window.scrollTo(0, 0);
        }
    }, [location]);

    const handleLogoClick = () => {
        setMobileMenuOpen(false);
        navigate('/');
    };

    const closeMobileMenu = () => {
        setMobileMenuOpen(false);
    };

    return (
        <>
            {/* Backdrop for drawer on desktop */}
            {mobileMenuOpen && (
                <div
                    className="splash-menu-backdrop"
                    onClick={closeMobileMenu}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'var(--overlay-bg)',
                        zIndex: 999,
                        opacity: 0,
                        animation: 'fadeIn 0.3s ease forwards',
                    }}
                />
            )}
            <style>{`
                @keyframes fadeIn {
                    to { opacity: 1; }
                }
                @media (max-width: 768px) {
                    .splash-menu-backdrop {
                        display: none !important;
                    }
                }
            `}</style>
            <header className={`splash-navbar ${scrolled ? 'scrolled' : ''}`}>
                <div className="splash-navbar-inner">
                    <button
                        onClick={handleLogoClick}
                        className="splash-logo-btn"
                        aria-label="Back to top"
                    >
                        <img
                            src={graspdLogo}
                            alt="Graspd"
                            className="splash-logo-full"
                        />
                    </button>

                    <nav className="splash-nav-links">
                            <Link to="/academy" className="splash-nav-link">
                                Academy
                            </Link>
                            <Link to="/pricing" className="splash-nav-link">
                                Plans
                            </Link>
                            <Link to="/" className="splash-nav-link">
                                Enterprise
                            </Link>
                            <a
                                href={APP_STORE_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="splash-nav-link"
                            >
                                App Store
                            </a>
                        </nav>

                    <div className="splash-nav-right">
                        <Link to="/login" className="splash-nav-link">
                            Log in
                        </Link>
                        <Link to="/login?signup=true" className="splash-nav-cta">
                            Get started
                        </Link>

                        <button
                                className="splash-mobile-menu-btn"
                                onClick={() => setMobileMenuOpen(true)}
                                aria-label="Open menu"
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="3" y1="6" x2="21" y2="6" />
                                    <line x1="3" y1="12" x2="21" y2="12" />
                                    <line x1="3" y1="18" x2="21" y2="18" />
                                </svg>
                            </button>
                    </div>
                </div>
            </header>

            {/* Mobile menu overlay */}
            <div className={`splash-mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`}>
                <div className="splash-mobile-menu-header">
                    <button
                        onClick={handleLogoClick}
                        className="splash-mobile-logo-btn"
                    >
                        <img src={graspdLogo} alt="Graspd" className="splash-logo-full" />
                    </button>
                    <button
                        className="splash-mobile-close-btn"
                        onClick={closeMobileMenu}
                        aria-label="Close menu"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                <nav className="splash-mobile-nav">
                    <Link
                        to="/academy"
                        className="splash-mobile-nav-link"
                        onClick={closeMobileMenu}
                    >
                        Academy
                    </Link>
                    <Link
                        to="/pricing"
                        className="splash-mobile-nav-link"
                        onClick={closeMobileMenu}
                    >
                        Plans
                    </Link>
                    <Link
                        to="/"
                        className="splash-mobile-nav-link"
                        onClick={closeMobileMenu}
                    >
                        Enterprise
                    </Link>
                </nav>

                {/* Mobile App Store Badge */}
                <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="splash-mobile-appstore-badge"
                    onClick={closeMobileMenu}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                    </svg>
                    <span>Download on App Store</span>
                </a>

                <Link
                    to="/login"
                    className="splash-mobile-login-btn"
                    onClick={closeMobileMenu}
                >
                    Log in
                </Link>

                <div className="splash-mobile-social">
                    <a
                        href="https://instagram.com/graspd.ai"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="splash-mobile-social-link"
                        aria-label="Instagram"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                        </svg>
                    </a>
                    <a
                        href="https://x.com/graspdai"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="splash-mobile-social-link"
                        aria-label="X (Twitter)"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                    </a>
                </div>
            </div>
        </>
    );
};

export default Navbar;
